import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewChild, signal } from '@angular/core';
import { ThreeDeePlotComponent } from './3dPlot/3Dplot.component';
import { TrajectoryService } from '../../../shared/services/trajectory.service';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { SelectItem } from 'primeng/api';
import { TrajectoryXyzModel } from 'src/app/shared/models/trajectory-xyz.model';
import { TrajectoryModel, TrajectoryPlotUi } from 'src/app/shared/models/trajectory.model';
import { Subscription, lastValueFrom } from 'rxjs';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { Store } from '@ngneat/elf';
import { StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { withUIEntities } from '@ngneat/elf-entities';
import { XyLinePlotUi } from 'src/app/core/models/xy-line-plot.model';
import { DlsOverridesService } from 'src/app/perivis/services/dls-override.service';
import { DlsOverrideModel } from 'src/app/shared/models/dlsOverride.model';


@Component({
    selector: 'app-trajectory-plot',
    templateUrl: './trajectory-plot-container.component.html',
  styles: [':host { display: flex; flex-direction: column; height: 100%; min-height: 0; overflow: hidden; }'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TrajectoryPlotContainerComponent implements OnInit, OnDestroy {
  private _subscriptions: Subscription = new Subscription();
  private _componentId = '';

  public readonly plotView: SelectItem[] = [
    { label: '3D', value: '3d' },
    { label: 'Northing/TVD', value: 'northingTvd' },
    { label: 'Easting/TVD', value: 'eastingTvd' },
    { label: 'Dogleg/MD', value: 'doglegMd' },
    { label: 'Plan View', value: 'planView' }
  ];

  //Used for component state
  @Input() set componentId(value: string) {
    this._componentId = value;
    this.trajectoryPlotStore.set(this._storeService.createStore(value, new TrajectoryPlotUi, withUIEntities<XyLinePlotUi>()));
  }
  get componentId(): string {
    return this._componentId;
  }
  
  // Signals for reactive state (Angular 21+ zoneless compatibility)
  public selectedPlotView = signal('3d');
  public isRotating = signal(false);
  public isShowingProjections = signal(false);
  public isLoading = signal(false);
  public trajectoryData = signal<TrajectoryModel[]>([]);
  public trajectoryXyzData = signal<TrajectoryXyzModel | null>(null);
  public dlsOverrides = signal<DlsOverrideModel[]>([]);
  public uu = signal<UserUnitsModel | null>(null);
  public longLengthUnits = signal('');
  public isDarkMode = signal(true);
  public trajectoryPlotStore = signal<Store | null>(null);

  @ViewChild('threeDPlot') public threeDPlot!: ThreeDeePlotComponent;

  constructor(
    private _trajectoryService: TrajectoryService,
    private _signalRService: SignalRService,
    private _storeService: StoreService,
    private _dlsOverrideService: DlsOverridesService
  ) { }

  async ngOnInit(): Promise<void> {
    const themeStore =this._storeService.getStore(StorageKeys.DARK_MODE).subscribe((d) => {
      this.isDarkMode.set(d.isDarkMode ?? true);
    });
    this._subscriptions.add(themeStore);

    const units = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);
    this.uu.set(units);
    this.longLengthUnits.set(units?.longLengths ?? '');

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));

    // Initialize signals from persisted store state
    const store = this.trajectoryPlotStore();
    if (store) {
      this.selectedPlotView.set(store.state.selectedPlotView ?? '3d');
      this.isRotating.set(store.state.isRotating ?? false);
      this.isShowingProjections.set(store.state.isShowingProjections ?? false);

      // Subscribe to store updates and sync to signals
      this._subscriptions.add(
        store.subscribe(state => {
          this.selectedPlotView.set(state.selectedPlotView ?? '3d');
          this.isRotating.set(state.isRotating ?? false);
          this.isShowingProjections.set(state.isShowingProjections ?? false);
        })
      );
    }

    await this.getPlotData(false);
  }

  signalRfunc(data: any) {
    if (data.action == PeriforOnChangeMessages.REFRESH_TRAJECTORY_PLOT) {
      void this.getPlotData(true);
    }
  }

  public async getPlotData(load: boolean) {
    this.isLoading.set(load);

    if (this.selectedPlotView() === '3d') {
      const data = await lastValueFrom(this._trajectoryService.getTrajectoryXyz());
      this.trajectoryXyzData.set(data);
    } else {
      const [data, overrides] = await Promise.all([
        lastValueFrom(this._trajectoryService.getTrajectoryPoints()),
        lastValueFrom(this._dlsOverrideService.getDlsOverrides())
      ]);
      this.trajectoryData.set(data);
      this.dlsOverrides.set(overrides || []);
    }

    this.isLoading.set(false);
  }

  public selectPlotView(e: any): void {
    const store = this.trajectoryPlotStore();
    if (!store) {
      return;
    }

    store.update((state) => ({
      ...state,
      selectedPlotView: e.value,
      UIEntities: []
    }));

    void this.getPlotData(true);
  }

  public onRotatingChange(e: any): void {
    const store = this.trajectoryPlotStore();
    if (!store) {
      return;
    }

    store.update((state) => ({
      ...state,
      isRotating: e.checked
    }));
    this.threeDPlot?.onRotationCheckChange(e);
  }

  public onProjectionsChange(e: any): void {
    const store = this.trajectoryPlotStore();
    if (!store) {
      return;
    }

    store.update((state) => ({
      ...state,
      isShowingProjections: e.checked
    }));
    this.threeDPlot?.onProjectionsCheckChange(e);
  }

  ngOnDestroy() {
    this._subscriptions?.unsubscribe();
  }
}
